import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useTranslation } from 'react-i18next';

const ENDPOINT_URL = '/users/toggle-unit';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type UpdateUnitInput = paths[url]['post'];

export interface UpdateUnitSuccessResponse {
    message: string;
}

export const useToggleUnit = () => {
    const { t } = useTranslation();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const request = async () => {
        setLoading(true);
        setError(null);

        try {
            const inputParameters = new URLSearchParams(getCommonParameters()).toString();
            const response = await fetch(`${API_URL}${ENDPOINT_URL}?${inputParameters}`, {
                method: 'POST',
                headers: getHeaders(),
            });

            const data: UpdateUnitSuccessResponse = await response.json();

            if (!response.ok) {
                const errorMessage = data.message || t('common.error-occurred');
                const error = new Error(errorMessage) as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            // no need to call setUser
            setSuccess(data.message);
            return data.message;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime, success };
};
